/**
 * Operator portal rework PR 9 (migration 0213) — an operator company's API keys, on the operator
 * page under its Plan card. Paelode issues, rotates and revokes them here; the operator admin does
 * the same in the portal, and the hauler only lists the keys reading its data (Corbin,
 * requirement 6). A hauler key reads ONE hauler's data, for a hauler confirmed in the Haulers card.
 * A company-wide key (13.9, D-08, DEC-372) is tied to no hauler: the producer's own SCADA or
 * integrator uses it to send tank readings (tanks:read, readings:write only).
 *
 * The secret is shown once, right after issuing or rotating, and never again.
 */
import * as React from 'react';
import { toast } from 'sonner';
import {
  HAULER_KEY_SCOPES,
  OPERATOR_KEY_SCOPES,
  type HaulerKeyScope,
  type OperatorKeyScope,
} from '@lodex-pro/shared/db/schema';
import type {
  IssuedOperatorApiKey,
  OperatorApiKeyView,
} from '@lodex-pro/shared/validation/operator-api-credentials';
import { Button } from 'src/components/ui/button';
import { FormRow, Pill, fieldStyle, subText } from '../../ui/bits';
import {
  COMPANY_KEY_SCOPE_LABELS,
  ISSUED_BY_LABELS,
  apiKeyError,
  keyKindLabel,
  isLiveKey,
  keyStatusLabel,
  useIssueOperatorApiKey,
  useOperatorApiKeyHaulers,
  useOperatorApiKeys,
  useRefreshOperatorApiKeys,
  useRevokeOperatorApiKey,
  useRotateOperatorApiKey,
} from './api-key-hooks';
import { formatNumber } from '@lodex-pro/shared/number-format';

const cardStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-bg-secondary)',
  borderRadius: 'var(--radius-card)',
  padding: '20px',
  marginBottom: '16px',
};
const cell: React.CSSProperties = {
  padding: '6px',
  borderTop: '1px solid var(--color-border-primary)',
  verticalAlign: 'top',
};

/** The secret, once, with what to do about it. */
function SecretOnce({
  issued,
  onDone,
}: {
  issued: IssuedOperatorApiKey;
  onDone: () => void;
}): React.JSX.Element {
  return (
    <div
      role="alert"
      style={{
        borderRadius: '12px',
        backgroundColor: 'var(--color-bg-tertiary)',
        padding: '12px',
        margin: '12px 0',
      }}
    >
      <p style={{ margin: '0 0 6px', fontWeight: 600 }}>
        {issued.key.keyKind === 'OPERATOR'
          ? 'The company-wide key.'
          : `The key for ${issued.key.haulerName ?? 'the hauler'}.`}{' '}
        Copy it now: it won’t be shown again.
      </p>
      <code
        data-testid="api-key-secret"
        style={{ display: 'block', wordBreak: 'break-all', fontSize: '13px', marginBottom: '8px' }}
      >
        {issued.secret}
      </code>
      {issued.replaced !== undefined && (
        <p style={{ ...subText, textTransform: 'none' }}>
          The key it replaces keeps working until{' '}
          {new Date(issued.replaced.expiresAt).toLocaleString()}.
        </p>
      )}
      <div style={{ display: 'flex', gap: '8px' }}>
        <Button
          type="button"
          variant="outline"
          onClick={(): void => {
            void navigator.clipboard?.writeText(issued.secret);
            toast.success('Copied');
          }}
        >
          Copy
        </Button>
        <Button type="button" onClick={onDone}>
          Done
        </Button>
      </div>
    </div>
  );
}

function IssueForm({
  tenantId,
  onIssued,
}: {
  tenantId: string;
  onIssued: (k: IssuedOperatorApiKey) => void;
}): React.JSX.Element {
  const haulers = useOperatorApiKeyHaulers(tenantId, true);
  const issue = useIssueOperatorApiKey(tenantId);
  const [kind, setKind] = React.useState<'HAULER_PAIR' | 'OPERATOR'>('HAULER_PAIR');
  const [haulerTenantId, setHauler] = React.useState('');
  const [label, setLabel] = React.useState('');
  const [scopes, setScopes] = React.useState<HaulerKeyScope[]>(['tickets:read']);
  // 13.9: a company-wide key offers only its two scopes, both checked to start with.
  const [companyScopes, setCompanyScopes] = React.useState<OperatorKeyScope[]>([
    ...OPERATOR_KEY_SCOPES,
  ]);
  const list = haulers.data?.haulers ?? [];
  const picked = list.find((h) => h.haulerTenantId === haulerTenantId);
  const companyWide = kind === 'OPERATOR';
  const noHaulers = !haulers.isLoading && list.length === 0;

  const kindChoice = (
    <fieldset style={{ border: 'none', padding: 0, margin: '0 0 8px' }}>
      <legend style={{ fontSize: '13px', marginBottom: '4px' }}>Kind of key</legend>
      {(
        [
          ['HAULER_PAIR', 'For a hauler'],
          ['OPERATOR', 'Company-wide (tank readings)'],
        ] as const
      ).map(([value, text]) => (
        <label key={value} style={{ display: 'inline-flex', gap: '4px', marginRight: '12px' }}>
          <input
            type="radio"
            name={`op-key-kind-${tenantId}`}
            value={value}
            checked={kind === value}
            onChange={(): void => setKind(value)}
          />
          {text}
        </label>
      ))}
    </fieldset>
  );

  const labelRow = (
    <FormRow label="Label" htmlFor="op-key-label">
      <input
        id="op-key-label"
        value={label}
        onChange={(e): void => setLabel(e.target.value)}
        placeholder="e.g. SCADA nightly"
        style={fieldStyle}
      />
    </FormRow>
  );

  if (companyWide) {
    return (
      <div style={{ marginTop: '12px' }}>
        {kindChoice}
        <p style={{ ...subText, textTransform: 'none' }}>
          For the producer’s SCADA or integrator to send tank levels or volumes. It can’t read
          hauler tickets or volumes.
        </p>
        {labelRow}
        <fieldset style={{ border: 'none', padding: 0, margin: '0 0 8px' }}>
          <legend style={{ fontSize: '13px', marginBottom: '4px' }}>What it can do</legend>
          {OPERATOR_KEY_SCOPES.map((s) => (
            <label key={s} style={{ display: 'inline-flex', gap: '4px', marginRight: '12px' }}>
              <input
                type="checkbox"
                checked={companyScopes.includes(s)}
                onChange={(e): void =>
                  setCompanyScopes((cur) =>
                    e.target.checked ? [...cur, s] : cur.filter((x) => x !== s),
                  )
                }
              />
              {COMPANY_KEY_SCOPE_LABELS[s]}
            </label>
          ))}
        </fieldset>
        <Button
          type="button"
          disabled={issue.isPending || label.trim().length < 2 || companyScopes.length === 0}
          onClick={(): void =>
            issue.mutate(
              { keyKind: 'OPERATOR', label, scopes: companyScopes },
              {
                onSuccess: (res) => {
                  setLabel('');
                  onIssued(res);
                },
                onError: (err) => toast.error(apiKeyError(err)),
              },
            )
          }
        >
          Issue key
        </Button>
      </div>
    );
  }

  if (noHaulers) {
    return (
      <div style={{ marginTop: '12px' }}>
        {kindChoice}
        <p style={{ ...subText, textTransform: 'none' }}>
          No hauler is confirmed for this operator yet. Add one in the Haulers card to issue a
          hauler key.
        </p>
      </div>
    );
  }
  return (
    <div style={{ marginTop: '12px' }}>
      {kindChoice}
      <FormRow label="Hauler" htmlFor="op-key-hauler">
        <select
          id="op-key-hauler"
          value={haulerTenantId}
          onChange={(e): void => setHauler(e.target.value)}
          style={fieldStyle}
        >
          <option value="">Choose a hauler</option>
          {list.map((h) => (
            <option key={h.haulerTenantId} value={h.haulerTenantId}>
              {h.haulerName}
              {h.hasCustomerMapping ? '' : ' (no customer mapping yet)'}
            </option>
          ))}
        </select>
      </FormRow>
      {picked !== undefined && !picked.hasCustomerMapping && (
        <p style={{ ...subText, textTransform: 'none' }}>
          {picked.haulerName} maps no customer to this operator yet, so the key will read nothing
          until it does.
        </p>
      )}
      {labelRow}
      <fieldset style={{ border: 'none', padding: 0, margin: '0 0 8px' }}>
        <legend style={{ fontSize: '13px', marginBottom: '4px' }}>What it can do</legend>
        {/* Never readings:write: that is a company-wide key's alone (13.9). */}
        {HAULER_KEY_SCOPES.map((s) => (
          <label key={s} style={{ display: 'inline-flex', gap: '4px', marginRight: '12px' }}>
            <input
              type="checkbox"
              checked={scopes.includes(s)}
              onChange={(e): void =>
                setScopes((cur) => (e.target.checked ? [...cur, s] : cur.filter((x) => x !== s)))
              }
            />
            {s}
          </label>
        ))}
      </fieldset>
      <Button
        type="button"
        disabled={
          issue.isPending || haulerTenantId === '' || label.trim().length < 2 || scopes.length === 0
        }
        onClick={(): void =>
          issue.mutate(
            { haulerTenantId, label, scopes },
            {
              onSuccess: (res) => {
                setLabel('');
                onIssued(res);
              },
              onError: (err) => toast.error(apiKeyError(err)),
            },
          )
        }
      >
        Issue key
      </Button>
    </div>
  );
}

function KeyActions({
  tenantId,
  k,
  onRotated,
  onChanged,
}: {
  tenantId: string;
  k: OperatorApiKeyView;
  onRotated: (r: IssuedOperatorApiKey) => void;
  onChanged: () => void;
}): React.JSX.Element | null {
  const rotate = useRotateOperatorApiKey(tenantId, k.id);
  const revoke = useRevokeOperatorApiKey(tenantId, k.id);
  const [reason, setReason] = React.useState('');
  const [revoking, setRevoking] = React.useState(false);
  if (!isLiveKey(k)) return null;
  return (
    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
      <Button
        type="button"
        variant="outline"
        disabled={rotate.isPending}
        onClick={(): void => {
          if (!window.confirm(`Rotate ${k.label}? The old key keeps working for 24 hours.`)) return;
          rotate.mutate(
            { overlapHours: 24 },
            {
              onSuccess: (r) => onRotated(r),
              onError: (err) => toast.error(apiKeyError(err)),
            },
          );
        }}
      >
        Rotate
      </Button>
      {revoking ? (
        <>
          <input
            className="keep-case"
            aria-label={`Why revoke ${k.label}`}
            value={reason}
            onChange={(e): void => setReason(e.target.value)}
            placeholder="Why (8+ characters)"
            style={{ ...fieldStyle, width: '220px' }}
          />
          <Button
            type="button"
            disabled={revoke.isPending || reason.trim().length < 8}
            onClick={(): void =>
              revoke.mutate(
                { reason: reason.trim() },
                {
                  onSuccess: () => {
                    toast.success(`${k.label} revoked. It stops working now.`);
                    setRevoking(false);
                    onChanged();
                  },
                  onError: (err) => toast.error(apiKeyError(err)),
                },
              )
            }
          >
            Revoke now
          </Button>
        </>
      ) : (
        <Button type="button" variant="outline" onClick={(): void => setRevoking(true)}>
          Revoke
        </Button>
      )}
    </div>
  );
}

export function OperatorApiKeysCard({ tenantId }: { tenantId: string }): React.JSX.Element {
  const { data, isLoading, isError } = useOperatorApiKeys(tenantId);
  const refresh = useRefreshOperatorApiKeys(tenantId);
  const [shown, setShown] = React.useState<IssuedOperatorApiKey | null>(null);
  const [issuing, setIssuing] = React.useState(false);
  const [showRevoked, setShowRevoked] = React.useState(false);

  const keys = (data?.keys ?? []).filter((k) => showRevoked || k.status === 'ACTIVE');
  const atLimit = data !== undefined && data.limit !== null && data.liveCount >= data.limit;
  const onSecret = (r: IssuedOperatorApiKey): void => {
    setShown(r);
    setIssuing(false);
    refresh();
  };

  return (
    <section style={cardStyle} aria-label="API keys">
      <h2 style={{ margin: '0 0 4px', fontSize: '16px' }}>API keys</h2>
      <p style={{ ...subText, textTransform: 'none', margin: '0 0 8px' }}>
        A hauler key reads one hauler’s data for this operator; a company-wide key sends the
        operator’s own tank readings. The operator’s admin can manage them in the portal too;
        haulers only see the keys reading their data.
      </p>
      {isLoading ? (
        <p style={subText}>Loading…</p>
      ) : isError || data === undefined ? (
        <p style={subText}>Could not load the API keys.</p>
      ) : (
        <>
          <p style={{ margin: '0 0 8px', fontSize: '14px' }}>
            {!data.apiEnabled
              ? 'API access (OP_API) is off on this plan: no key can be issued, and existing keys are refused.'
              : data.limit === null
                ? `${formatNumber(data.liveCount)} live keys · no limit`
                : `${formatNumber(data.liveCount)} of ${data.limit} live keys in use`}
          </p>
          {shown !== null && <SecretOnce issued={shown} onDone={(): void => setShown(null)} />}
          {keys.length === 0 ? (
            <p style={{ ...subText, textTransform: 'none' }}>No keys yet.</p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ textAlign: 'left' }}>
                  <th style={cell}>Kind</th>
                  <th style={cell}>Label</th>
                  <th style={cell}>Can</th>
                  <th style={cell}>Status</th>
                  <th style={cell}>Last used</th>
                  <th style={cell}>Calls this month</th>
                  <th style={cell}>Issued by</th>
                  <th style={cell} />
                </tr>
              </thead>
              <tbody>
                {keys.map((k) => (
                  <tr key={k.id}>
                    <td style={cell}>
                      {keyKindLabel(k)}
                      {k.haulerConfirmed === false && (
                        <div>
                          <Pill label="Hauler removed: refused" tone="danger" />
                        </div>
                      )}
                    </td>
                    <td style={cell}>{k.label}</td>
                    <td style={cell}>{k.scopes.join(', ')}</td>
                    <td style={cell}>{keyStatusLabel(k)}</td>
                    <td style={cell}>
                      {k.lastUsedAt === null ? 'Never' : new Date(k.lastUsedAt).toLocaleString()}
                    </td>
                    <td style={cell}>
                      {Number(k.monthRequests).toLocaleString()}
                      {k.monthWrites !== '0' &&
                        ` (${Number(k.monthWrites).toLocaleString()} writes)`}
                    </td>
                    <td style={cell}>{ISSUED_BY_LABELS[k.issuedByActor]}</td>
                    <td style={cell}>
                      <KeyActions
                        tenantId={tenantId}
                        k={k}
                        onRotated={onSecret}
                        onChanged={refresh}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <label style={{ display: 'inline-flex', gap: '4px', margin: '8px 0', fontSize: '13px' }}>
            <input
              type="checkbox"
              checked={showRevoked}
              onChange={(e): void => setShowRevoked(e.target.checked)}
            />
            Show revoked
          </label>
          {data.apiEnabled &&
            (atLimit ? (
              <p style={{ ...subText, textTransform: 'none' }}>
                All {data.limit} keys this plan allows are in use. Revoke one, or raise the OP_API
                limit in the Plan card, to issue another.
              </p>
            ) : issuing ? (
              <IssueForm tenantId={tenantId} onIssued={onSecret} />
            ) : (
              <div>
                <Button type="button" onClick={(): void => setIssuing(true)}>
                  Issue a key
                </Button>
              </div>
            ))}
        </>
      )}
    </section>
  );
}
